import React from 'react';
import { Building2 } from 'lucide-react';
import { colors, borderRadius } from '../../styles/tokens';
import { useBlockEditor } from '../../hooks/useBlockEditor';
import { EditableField } from '../editing';

const MEAL_PLAN_OPTIONS = [
  { value: '', label: 'Não informado' },
  { value: 'sem_refeicao', label: 'Sem refeição' },
  { value: 'cafe_da_manha', label: 'Café da manhã' },
  { value: 'meia_pensao', label: 'Meia pensão' },
  { value: 'pensao_completa', label: 'Pensão completa' },
  { value: 'all_inclusive', label: 'All Inclusive' },
];

export default function HotelCard({ block, onRemove, quoteId, onBlockUpdate }) {
  const editable = !!quoteId;
  const { saving, updateField, updateDetail, updateDetails, isSaved } =
    useBlockEditor(quoteId, block, onBlockUpdate);

  const details = block.details || {};

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('pt-BR');
  };

  // Recalcula nights ao mudar datas
  const handleCheckinChange = async (v) => {
    const updates = { checkin_date: v };
    if (details.checkout_date) {
      const nights = calcNights(v, details.checkout_date);
      if (nights > 0) updates.nights = nights;
    }
    await updateDetails(updates, 'checkin_date');
  };

  const handleCheckoutChange = async (v) => {
    const updates = { checkout_date: v };
    const checkin = details.checkin_date || block.date;
    if (checkin) {
      const nights = calcNights(checkin, v);
      if (nights > 0) updates.nights = nights;
    }
    await updateDetails(updates, 'checkout_date');
  };

  const E = (value, onSave, opts = {}) => {
    if (!editable) return opts.fallback || value || opts.placeholder || '-';
    return (
      <EditableField
        value={value}
        onSave={onSave}
        type={opts.type || 'text'}
        options={opts.options}
        saving={saving}
        saved={opts.saved}
        placeholder={opts.placeholder || '-'}
        style={opts.style}
        validate={opts.validate}
      />
    );
  };

  return (
    <div style={{
      backgroundColor: colors.card, border: `1px solid ${colors.border}`,
      borderRadius: borderRadius.md, padding: '1.5rem', position: 'relative',
    }}>
      <button onClick={() => onRemove(block.id)} style={{
        position: 'absolute', top: '1rem', right: '1rem',
        background: 'none', border: 'none', cursor: 'pointer',
        color: colors.text.muted, fontSize: '1.25rem',
      }}>×</button>

      <div style={{ display: 'flex', gap: '1rem' }}>
        <div style={{
          width: '48px', height: '48px', backgroundColor: `${colors.primary}10`,
          borderRadius: borderRadius.sm, display: 'flex', alignItems: 'center',
          justifyContent: 'center', flexShrink: 0,
        }}>
          <Building2 size={24} color={colors.primary} />
        </div>

        <div style={{ flex: 1 }}>
          <h4 style={{ fontSize: '1.125rem', fontWeight: 600, color: colors.primary, margin: '0 0 1rem 0' }}>
            {E(block.title, (v) => updateField('title', v), { saved: isSaved('title'), fallback: block.title })}
          </h4>

          {details.stars ? (
            <p style={{ fontSize: '0.875rem', color: colors.text.muted, margin: '-0.75rem 0 0.75rem 0' }}>
              {'\u2b50'.repeat(details.stars)}
            </p>
          ) : null}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            {/* Tipo de acomodação */}
            <div>
              <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Tipo de acomodação</p>
              <p style={{ fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary, margin: 0 }}>
                {E(details.room_type || block.description, (v) => updateDetail('room_type', v), {
                  saved: isSaved('room_type'), fallback: details.room_type || block.description || '-',
                })}
              </p>
            </div>

            {/* Regime de refeição */}
            <div>
              <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Refeição</p>
              <p style={{ fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary, margin: 0 }}>
                {E(details.meal_plan || '', (v) => updateDetail('meal_plan', v), {
                  type: 'select', options: MEAL_PLAN_OPTIONS,
                  saved: isSaved('meal_plan'),
                  fallback: MEAL_PLAN_OPTIONS.find(o => o.value === details.meal_plan)?.label || details.meal_plan || '-',
                })}
              </p>
            </div>

            {/* Check-in */}
            <div>
              <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Check-in</p>
              <p style={{ fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary, margin: 0 }}>
                {E(details.checkin_date || block.date, handleCheckinChange, {
                  type: 'date', saved: isSaved('checkin_date'),
                  fallback: formatDate(details.checkin_date || block.date),
                })}
              </p>
            </div>

            {/* Check-out */}
            <div>
              <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Check-out</p>
              <p style={{ fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary, margin: 0 }}>
                {E(details.checkout_date, handleCheckoutChange, {
                  type: 'date', saved: isSaved('checkout_date'),
                  fallback: formatDate(details.checkout_date),
                  validate: (v) => {
                    const checkin = details.checkin_date || block.date;
                    if (checkin && v < checkin) return 'Antes do check-in';
                    return null;
                  },
                })}
              </p>
            </div>

            {/* Noites (read-only, calculado) */}
            {details.nights && (
              <div>
                <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Noites</p>
                <p style={{ fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary, margin: 0 }}>
                  {details.nights}
                </p>
              </div>
            )}

            {/* Valor */}
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Valor</p>
              <p style={{ fontSize: '1.25rem', fontWeight: 700, color: colors.accent, margin: 0 }}>
                {E(block.price || 0, (v) => updateField('price', v), {
                  type: 'currency', saved: isSaved('price'),
                  validate: (v) => v < 0 ? 'Preço inválido' : null,
                  fallback: `R$ ${(block.price || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
                })}
              </p>
            </div>
          </div>

          <DadosDoFornecedor details={details} />
        </div>
      </div>
    </div>
  );
}

/**
 * Dados que o fornecedor entrega e o vendedor precisa ver — somente leitura.
 *
 * Tudo aqui e opcional POR CONSTRUCAO: bloco antigo (extracao de PDF) nao tem
 * nenhuma destas chaves, e a ausencia nao pode quebrar o card. Por isso cada
 * pedaco testa o proprio dado antes de existir na tela.
 *
 * `charges_at_destination` aparece SEPARADO do valor e nunca e somado a ele:
 * e o que o hospede paga no balcao do hotel. Some-los seria dizer ao vendedor
 * um preco que a DSC nao cobra.
 */
function DadosDoFornecedor({ details }) {
  const camas = Array.isArray(details.bed_options) ? details.bed_options : [];
  const cobrancas = Array.isArray(details.charges_at_destination)
    ? details.charges_at_destination
    : [];
  const temCancelamento = !!(details.cancellation_policy || details.cancellation_status);

  if (!details.room_description && camas.length === 0 && !details.price_per_night
      && !temCancelamento && cobrancas.length === 0) {
    return null;
  }

  const rotulo = { fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' };
  const valor = { fontSize: '0.875rem', color: colors.text.primary, margin: 0 };

  return (
    <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {details.room_description ? (
        <div>
          <p style={rotulo}>Descricao do quarto</p>
          <p style={valor}>{details.room_description}</p>
        </div>
      ) : null}

      {camas.length > 0 ? (
        <div>
          <p style={rotulo}>Camas</p>
          {/* Texto do fornecedor, verbatim: dois formatos ja observados. */}
          {camas.map((cama, i) => (
            <p key={i} style={valor}>{cama}</p>
          ))}
        </div>
      ) : null}

      {details.price_per_night ? (
        <div>
          <p style={rotulo}>Por noite</p>
          <p style={valor}>
            {`R$ ${Number(details.price_per_night).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
          </p>
        </div>
      ) : null}

      {temCancelamento ? (
        <div>
          <p style={rotulo}>Cancelamento</p>
          {details.cancellation_policy ? (
            <p style={valor}>{details.cancellation_policy}</p>
          ) : null}
          {details.cancellation_status ? (
            <p style={{ ...valor, fontSize: '0.75rem', color: colors.text.muted }}>
              {details.cancellation_status}
            </p>
          ) : null}
        </div>
      ) : null}

      {cobrancas.length > 0 ? (
        <div>
          <p style={rotulo}>Cobranca no destino — fora do valor acima</p>
          {cobrancas.map((c, i) => (
            <p key={i} style={valor}>
              {c.description}
              {c.value != null
                ? ` — R$ ${Number(c.value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                : ''}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function calcNights(checkin, checkout) {
  const a = new Date(checkin + 'T00:00:00');
  const b = new Date(checkout + 'T00:00:00');
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
}
